import { assertNever } from '@spiaggeit/spit-core'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Minus } from '@/assets/icons/Minus'
import { Plus } from '@/assets/icons/Plus'

import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { Service } from '@/models/service'
import { cartSlice } from '@/store/cartSlice'
import { ticketPageErrorsSelector } from '@/store/cartSlice/selectors/ticketPageErrorsSelector'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import { ticketSlice } from '@/store/ticketSlice'
import { priceFormatter } from '@/utils/price'

import { getDaysFromPriceMode, getTimeScheduleKey } from '@/utils/timeSchedules'
import { DangerousHtml } from '../DangerousHtml/DangerousHtml'
import { IconButton } from '../IconButton'

import { TimeScheduleDialogTrigger } from '../TimeScheduleDialog/TimeScheduleDialogTrigger'
import styles from './TicketDescription.module.css'

type TicketCardProps = {
  isAddDisable: boolean
  isRemoveDisabled: boolean
  maxLabel: string
  minLabel: string
  quantity: number
  ticket: Service
}

export function TicketCard(props: TicketCardProps) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const dayDifference = useAppSelector(dayDifferenceSelector)
  const submitted = useAppSelector(ticketSlice.selectors.submitted)
  const errors = useAppSelector(ticketPageErrorsSelector)

  const error = useMemo<string | null>(() => {
    if (!submitted) {
      return null
    }

    const err = errors.find((e) => e.serviceId === props.ticket.serviceId)
    if (!err) {
      return null
    }

    if (err.type === 'minQty') {
      return t(`ticketErrors.${err.type}`, { min: err.minQty })
    }
    return null
  }, [errors, submitted, props.ticket.serviceId])

  const showTimeScheduleTrigger = props.ticket.scheduleType !== 'daily'

  const minMaxLabel = useMemo<string>(() => {
    return [
      ...(props.ticket.minimumQuantity > 0 ? [props.minLabel] : []),
      ...(props.ticket.hasLimit && props.maxLabel ? [props.maxLabel] : []),
    ].join(' · ')
  }, [props.ticket])

  function updateQuantity(bought: number) {
    switch (props.ticket.scheduleType) {
      case 'daily': {
        dispatch(
          cartSlice.actions.addTimeScheduleToCart({
            boughtTimeSchedules: {
              [getTimeScheduleKey(props.ticket.timeSchedules[0])]: {
                bought,
                days: getDaysFromPriceMode(props.ticket, dayDifference),
                serviceId: props.ticket.serviceId,
                serviceName: props.ticket.name,
                timeSchedule: props.ticket.timeSchedules[0],
              },
            },
            reservation: {
              spotName: null,
              spotType: null,
            },
            service: props.ticket,
          })
        )
        break
      }
      case 'shifted':
      case 'custom': {
        // NOOP:
        break
      }
      default: {
        assertNever(props.ticket.scheduleType)
      }
    }
  }

  return (
    <div
      className="flex flex-col overflow-hidden rounded border bg-white shadow"
      data-testid="ticket-card"
    >
      {props.ticket.image ? (
        <img
          alt={props.ticket.name}
          className="block h-44 w-full object-cover"
          height="176"
          src={props.ticket.image}
          width="374"
        />
      ) : null}

      <div className="flex grow flex-col space-y-2 break-words p-4">
        <h3 className="font-bold">{props.ticket.name}</h3>

        {props.ticket.description ? (
          <DangerousHtml
            className={styles['ticket-description']}
            html={props.ticket.description}
          />
        ) : null}

        {!showTimeScheduleTrigger ? (
          <div className="flex items-center justify-between gap-2 pt-2">
            <div className="min-w-0">
              <div className="text-lg font-bold">
                {priceFormatter.format(props.ticket.price)}
              </div>

              {minMaxLabel ? (
                <div className="text-sm">{minMaxLabel}</div>
              ) : null}
            </div>

            <div className="flex shrink-0">
              <IconButton
                ariaLabel={t('itemQuantityController.remove')}
                data-testid="ticket-minus"
                icon={Minus}
                isDisabled={props.isRemoveDisabled}
                onClick={() => {
                  updateQuantity(props.quantity - 1)
                }}
              />

              <div className="flex w-10 items-center justify-center px-0.5 font-medium">
                {props.quantity}
              </div>

              <IconButton
                ariaLabel={t('itemQuantityController.add')}
                data-testid="ticket-plus"
                icon={Plus}
                isDisabled={props.isAddDisable}
                onClick={() => {
                  updateQuantity(props.quantity + 1)
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            {minMaxLabel ? (
              <div className="shrink-0 text-sm">{minMaxLabel}</div>
            ) : null}

            <TimeScheduleDialogTrigger ticket={props.ticket} />
          </div>
        )}

        {error ? <div className="text-sm text-red-600">{error}</div> : null}
      </div>
    </div>
  )
}
